/**
 * Live figures: polls /api/live while the page is visible and passes each snapshot to subscribers over the
 * AnJS event bus. Pages arrive already rendered with the latest figures, so the first poll waits one interval.
 * A failed read keeps the last good snapshot; nothing is ever invented.
 */

// Dependencies
import $ from 'almostnojs';

// How often to refresh.
const INTERVAL_MS = 30000;

// Latest snapshot and poller state.
let last = null, started = false;

/**
 * Fetch the latest snapshot and announce it.
 *
 * @returns {Promise<void>}
 */
async function load() {

    // Read; a failure keeps what we have.
    try {
        last = await $.get('/api/live', { timeout: 8000 });
        $.emit('live', last);
    } catch {
        // Keep the last good snapshot.
    }
}

/**
 * Start polling (once).
 *
 * @returns {void}
 */
function start() {

    // Only while someone is looking.
    setInterval(() => { if (document.visibilityState === 'visible') load(); }, INTERVAL_MS);

    // Catch up when the tab comes back.
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') load(); });
}

/**
 * Subscribe to live snapshots.
 *
 * @param {Function} handler - Called with each snapshot ({available, receivedAt, facts}).
 * @returns {Function} Unsubscribe.
 */
export function subscribe(handler) {

    // Listen on the bus.
    $.listen('live', handler);

    // Replay the latest, if any.
    if (last) handler(last);

    // First subscriber starts the poller.
    if (!started) { started = true; start(); }

    // Disposer.
    return () => $.forget('live', handler);
}

/**
 * "Updated 2 min ago", from an ISO time (matches the server's wording).
 *
 * @param {string} iso - Time.
 * @returns {string} Text.
 */
export function updatedAgo(iso) {

    // Whole seconds since.
    const s = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));

    // Coarsest unit that fits.
    if (s < 60) return `Updated ${s}s ago`;
    if (s < 3600) return `Updated ${Math.round(s / 60)} min ago`;
    if (s < 86400) return `Updated ${Math.round(s / 3600)} h ago`;
    return `Updated ${Math.round(s / 86400)} d ago`;
}
